import { Graphics } from '@inlet/react-pixi';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import type { Graphics as PixiGraphicsObject } from 'pixi.js';
import { useCallback, useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import type { ChatExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextBreak } from 'flow-builder/node/textBreak';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import WidgetCustomizationModal from '../../../containers/modals/widgetcustomization';
import {
  LocalizationLabelsT,
  LocalizationsT,
  PositionLabelsT,
  Positions,
  Views,
  ViewTypes,
} from '../../types';

export type Position = {
  position: Positions;
  view?: Views;
};

type MinimizedType = {
  html_text_offline: string;
  html_text_online: string;
};

export type Minimized = {
  minimized: MinimizedType;
};

export type Theme = {
  color_darken: string;
  color_lighten: string;
  color: string;
  text_color: string;
};

export type LocalizationsType = {
  value: LocalizationsT;
  label: LocalizationLabelsT;
};

export type LocalizationsPayload = Record<string, Minimized>;

export type PositionOption = {
  value: Positions;
  label: PositionLabelsT;
};

export type ViewT = Record<ViewTypes, Position>;

export type Payload = {
  loaded?: boolean;
  localizations: LocalizationsPayload;
  medium: ViewT;
  nextNodeId: string;
  small: ViewT;
  theme: Theme;
};

type WidgetCustomizationNodeAttributes = GenericNodeAttributes<
  Payload,
  ChatExtraData
>;
type WidgetCustomizationNodeProps = GenericNodeProps<Payload, ChatExtraData>;

const translatePayload: WidgetCustomizationNodeAttributes['translatePayload'] =
  (node) => {
    const { nextNodeId } = node.payload;

    return [
      {
        id: uuid(),
        label: 'arrow',
        connection: nextNodeId,
      },
    ];
  };

const updateConnection: WidgetCustomizationNodeAttributes['updateConnection'] =
  (node, _connection, connectionId) => {
    const payload = { ...node.payload, nextNodeId: connectionId };

    return {
      ...node,
      payload,
    };
  };

export const WidgetCustomizationNodeAttributes: WidgetCustomizationNodeAttributes =
  {
    iconName: 'widget-customize',
    label: 'Customization',
    color: hexStringToNumber(flowNodeColor.blue),
    translatePayload,
    updateConnection,
    NodeContent: WidgetCustomizationNode,
    ActionModal: WidgetCustomizationModal,
  };

export function WidgetCustomizationNode({
  node,
}: WidgetCustomizationNodeProps): JSX.Element {
  const { payload } = node;
  const { theme, medium, small } = payload;
  const mobilePosition = small?.minimized_view.position.replace('_', ' ');
  const desktopPosition = medium?.minimized_view.position.replace('_', ' ');

  if (!small || !medium)
    Sentry.captureMessage('Invalid Widgetcustomization', {
      contexts: {
        node: {
          ...node,
          payload: JSON.stringify(payload),
        },
      },
      tags: { team: SentryTeam.Routing },
    });

  const drawThemePill = useCallback(
    (graphics: PixiGraphicsObject) => {
      graphics.clear();
      graphics.beginFill(hexStringToNumber(theme.color));
      graphics.drawRoundedRect(85, 6, 30, 12, 6);
      graphics.endFill();
    },
    [theme],
  );

  return (
    <TextContent>
      {useMemo(
        () => (
          <>
            <TextEllipsis>
              <Text weight="normal" text="Theme color:" />
            </TextEllipsis>
            <Graphics draw={drawThemePill} />
            <TextBreak offset={{ x: 0, y: 5 }}>
              <Text weight="normal" text="Position:" />
              <Text
                weight="semibold"
                text={`${mobilePosition} on mobile and ${desktopPosition} on desktop`}
              />
            </TextBreak>
          </>
        ),
        [drawThemePill, mobilePosition, desktopPosition],
      )}
    </TextContent>
  );
}
